import {api} from "../../api.ts";
import {unwrapData} from "../../apiResponse.ts";
import type {Msisdn} from "../Msisdns/msisdnService.ts";

export interface GoogleUser {
    id?: number;
    firstName: string;
    lastName: string;
    email: string;
    avatarUrl: string | null;
    position?: string | null;
    externalMsisdn?: number | null;
}

export interface GoogleUsersResponse {
    googleResponse: {
        users: GoogleUser[];
        nextPage?: string | null;
    };
    alreadyExists: string[];
    alreadySynced: string[];
    integrationId: number;
}

export interface GoogleWorkspaceIntegration {
    id: number;
    platform: string;
    details: {
        userEmail: string;
        disabled: boolean;
        verified?: boolean;
    };
}

export interface GoogleIntegrationsResponse {
    current: GoogleWorkspaceIntegration | null;
    past: GoogleWorkspaceIntegration[];
}

export type GoogleUserImportStatus = 'WAITING' | 'SUCCESS' | 'UPDATED' | 'SKIPPED' | 'PARTIAL' | 'FAILED';

export interface GoogleUserImportResult {
    email: string;
    status: GoogleUserImportStatus;
    message?: string | null;
}

export interface GoogleImportStatusResponse {
    integrationId: number;
    entitiesCreated: number;
    entitiesRejected: number;
    entitiesAll: number;
    status: 'STARTED' | 'IN_PROGRESS' | 'FINISHED' | 'FAILED';
    entitiesStatuses: GoogleUserImportResult[];
}

export interface StartGoogleImportResponse {
    importId: number;
}

export interface ContactsImportConfig {
    id: number;
    type: "CONTACTS_IMPORT";
    platform: "PIPEDRIVE";
    details: {
        type: string;
        companyName: string;
        companyId: string;
    };
    configuration: {
        type: string;
        sendRecordings: boolean;
        sendCallSummary: boolean;
        sendMissedCalls: boolean;
        defaultNumbersPrefix?: string;
        sendCallTranscription: boolean;
        matchWithDeals: boolean;
    };
}
export type PhoneNumberType = 'ALL' | 'SOME';

export interface WebhookEventType {
    type: string;
    phoneNumbers?: {
        type: PhoneNumberType;
        phoneNumberIds?: number[];
    };
}

export type WebhookStatus = 'ENABLED' | 'DISABLED';

export interface WebhookForm {
    name: string;
    status: WebhookStatus;
    url: string;
    eventTypes: WebhookEventType[];
    technical: boolean;
}

export type WebhookUpdate = Partial<Pick<WebhookForm, 'name' | 'status' | 'url' | 'eventTypes'>>;

export interface WebhookConfiguration {
    status: WebhookStatus;
    secretKey: string;
}

export type WebhookHealthState = 'HEALTHY' | 'FAILING' | 'DISABLED';

export interface WebhookHealth {
    state: WebhookHealthState;
    failingSince: string | null;
    lastSuccessAt: string | null;
    lastFailureAt: string | null;
    lastError: string | null;
    disabledReason: 'AUTO_FAILING' | null;
    disabledAt: string | null;
    notifiedAt: string | null;
    autoDisableAt: string | null;
}

export type WebhookDeliveryStatus = 'PENDING' | 'SENDING' | 'SUCCEEDED' | 'FAILED';

export interface Webhook {
    id: number;
    name: string;
    url: string;
    eventTypes: WebhookEventType[];
    status: WebhookStatus;
    health: WebhookHealth;
    phoneNumbers?: Msisdn[];
    recentDeliveries?: WebhookDeliveryStatus[];
}

export interface WebhookDeliveryStats {
    pending: number;
    sending: number;
    succeeded: number;
    failed: number;
}

export interface WebhookDelivery {
    id: string;
    webhookId: number;
    eventType: string;
    status: WebhookDeliveryStatus;
    attempts: number;
    nextAttemptAt: string | null;
    lastStatusCode: number | null;
    lastError: string | null;
    createdAt: string;
    finishedAt: string | null;
    waitingFor: 'RECORDING' | 'CALLSCRIBE' | null;
}

export interface WebhookDeliveryAttempt {
    attempt: number;
    trigger: 'SCHEDULED' | 'MANUAL';
    startedAt: string;
    durationMs: number;
    statusCode: number | null;
    error: string | null;
    responseBody: string | null;
}

export interface WebhookDeliveryDetails {
    delivery: WebhookDelivery;
    payload: unknown;
    attempts: WebhookDeliveryAttempt[];
}

export interface WebhookDeliveriesArgs {
    id: number;
    statuses?: WebhookDeliveryStatus[];
    from?: string;
    offset: number;
    limit: number;
}

export enum IntegrationStatus {
    RUNNING = 'RUNNING',
    FINISHED = 'FINISHED',
    FAILED = 'FAILED',
}
export interface IntegrationImport {
    id: number;
    integrationType: string;
    operationType: string;
    operationStatus: IntegrationStatus;
    startDate: string;
    finishDate: string;
    entitiesCreated: number;
    entitiesUpdated: number;
    entitiesRejected: number;
    entitiesAll: number;
}

export interface IntegrationImportsResponse {
    data: IntegrationImport[];
}

export interface ApiKeySummary {
    id: number;
    name: string;
    status: IntegrationStatusEnum;
    apiKeyShortcut: string;
}

export interface CreatedApiKey extends ApiKeySummary {
    apiKey: string;
}

export interface ApiKeyCreateRequest {
    name: string;
    status?: IntegrationStatusEnum;
}

export enum IntegrationStatusEnum {
    ENABLED = 'ENABLED',
    DISABLED = 'DISABLED',
}

export interface ApiKeyUpdateRequest {
    id: number;
    name?: string;
    status: IntegrationStatusEnum;
}

export const integrationApi = api.injectEndpoints({
    endpoints: (builder) => ({
        startIntegrationGoogle: builder.mutation<GoogleUsersResponse, { email: string }>({
            query: ({email}) => ({
                url: '/api/v1/integrations/google',
                method: 'POST',
                body: {email},
            }),
            transformResponse: (response: GoogleUsersResponse | { data: GoogleUsersResponse }) => unwrapData(response),
            invalidatesTags: ['Integration']
        }),
        startIntegrationPipedrive: builder.mutation<GoogleUsersResponse, { code: string }>({
            query: ({code}) => ({
                url: '/api/v1/integrations/pipedrive',
                method: 'POST',
                body: {code},
            }),
            transformResponse: (response: GoogleUsersResponse | { data: GoogleUsersResponse }) => unwrapData(response),
            invalidatesTags: ['Integration']
        }),
        importGoogleContacts: builder.mutation<StartGoogleImportResponse, { integration: number; users: GoogleUser[] }>({
            query: ({integration, users}) => ({
                url: `/api/v1/integrations/google/${integration}/users`,
                method: 'POST',
                body: {integration, users},
            }),
            transformResponse: (response: StartGoogleImportResponse | { data: StartGoogleImportResponse }) => unwrapData(response),
        }),
        deleteGoogleIntegration: builder.mutation<void, { integrationId: number }>({
            query: ({integrationId}) => ({
                url: `/api/v1/integrations/google/${integrationId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Integration','CalendarSettings']
        }),
        deleteIntegration: builder.mutation<any, { integrationId: number }>({
            query: ({integrationId}) => ({
                url: `/api/v1/integrations/${integrationId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Integration', 'Settings', 'CalendarSettings']
        }),
        getIntegrations: builder.query<any, void>({
            query: () => ({
                url: `/api/v1/integrations/`,
                method: 'GET',
            }),
            providesTags: ['Integration']
        }),
        getCurrentGoogleIntegration: builder.query<GoogleIntegrationsResponse, void>({
            query: () => ({
                url: `/api/v1/integrations/google/current`,
                method: 'GET',
            }),
            transformResponse: (response: GoogleIntegrationsResponse | { data: GoogleIntegrationsResponse }) => unwrapData(response),
            providesTags: ['Integration'],
        }),
        getCurrentGoogleIntegrationUsers: builder.query<GoogleUsersResponse, { integrationId: number }>({
            query: ({integrationId}) => ({
                url: `/api/v1/integrations/google/${integrationId}/users`,
                method: 'GET',
            }),
            transformResponse: (response: GoogleUsersResponse | { data: GoogleUsersResponse }) => unwrapData(response),
        }),
        getPipedriveSettings: builder.query<any, { integrationId: number }>({
            query: ({integrationId}) => ({
                url: `api/v1/integrations/${integrationId}`,
                method: 'GET',
            }),
            transformResponse: (response: any) => unwrapData(response),
            providesTags: ['Settings']
        }),
        statusGoogleIntegration: builder.mutation<GoogleImportStatusResponse, {
            integrationId: number,
            emails: string[],
            importId: number | null
        }>({
            query: ({integrationId, emails, importId}) => ({
                url: `/api/v1/integrations/google/${integrationId}/status`,
                method: 'POST',
                params: {importId},
                body: {emails}
            }),
            transformResponse: (response: GoogleImportStatusResponse | { data: GoogleImportStatusResponse }) => unwrapData(response),
        }),
        patchGoogleIntegration: builder.mutation<any, { integrationId: number, emails: string[] }>({
            query: ({integrationId, emails}) => ({
                url: `/api/v1/integrations/google/${integrationId}/patch`,
                method: 'PATCH',
                body: {emails}
            }),
            transformResponse: (response: any) => unwrapData(response),
        }),
        patchPipedriveIntegration: builder.mutation<any, { integrationId: number, body: ContactsImportConfig }>({
            query: ({integrationId, body}) => ({
                url: `/api/v1/integrations/${integrationId}`,
                method: 'PATCH',
                body: body
            }),
            transformResponse: (response: any) => unwrapData(response),
            invalidatesTags: ['Settings']
        }),
        patchPipedrivePrefix: builder.mutation<any, { id: number, body: {defaultNumbersPrefix: string} }>({
            query: ({id, body}) => ({
                url: `/api/v1/integrations/${id}/default-numbers-prefix`,
                method: 'PATCH',
                body: body
            }),
            transformResponse: (response: any) => unwrapData(response),
            invalidatesTags: ['Settings']
        }),
        getIntegrationImports: builder.query<IntegrationImportsResponse, {
            integrationType?: string;
            sortBy?: string;
            order?: string;
            limit?: number;
        }>({
            query: (params) => ({
                url: '/api/v1/integration-imports',
                method: 'GET',
                params: params,
            }),

        }),
        getWebhooks: builder.query<{data: Webhook[]}, {name?: string, include: string}>({
            query: ({name, include}) => ({
                url: `api/v1/webhooks`,
                method: 'GET',
                params: {
                    include,
                    name
                }
            }),
            providesTags: ['Webhooks']

        }),
        getWebhook: builder.query<Webhook, {id: number}>({
            query: ({id}) => ({
                url: `api/v1/webhooks/${id}`,
                method: 'GET',
            }),
            transformResponse: (response: Webhook | { data: Webhook }) => unwrapData(response),
            providesTags: (_result, _error, {id}) => [
                { type: 'Webhooks', id },
                'Webhooks'
            ],
        }),
        updateWebhook: builder.mutation<Webhook, {id: number, data: WebhookUpdate}>({
            query: ({id, data}) => ({
                url: `api/v1/webhooks/${id}`,
                method: 'PUT',
                body: data,
            }),
            transformResponse: (response: Webhook | { data: Webhook }) => unwrapData(response),
            invalidatesTags: (_result, _error, {id}) => [
                { type: 'Webhooks', id },
                'Webhooks'
            ]
        }),
        createWebhook: builder.mutation<Webhook, {data: WebhookForm}>({
            query: ({data}) => ({
                url: 'api/v1/webhooks',
                method: 'POST',
                body: data,
            }),
            transformResponse: (response: Webhook | { data: Webhook }) => unwrapData(response),
            
            invalidatesTags: ['Webhooks']
        }),
        deleteWebhook: builder.mutation<null, { id: number }>({
            query: ({id}) => ({
                url: `api/v1/webhooks/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, {id}) => [
                { type: 'Webhooks', id: id },
                'Webhooks'
            ]
        }),
        getWebhookDeliveries: builder.query<{data: WebhookDelivery[]; totalCount: number}, WebhookDeliveriesArgs>({
            query: ({id, statuses, ...params}) => ({
                url: `api/v1/webhooks/${id}/deliveries`,
                method: 'GET',
                params: {...params, statuses: statuses?.join(',')},
            }),
            transformResponse: (response: {data: WebhookDelivery[]}, meta) => {
                const totalCount = meta?.response?.headers.get('x-total-count');
                return {data: response.data, totalCount: totalCount ? parseInt(totalCount, 10) : response.data.length};
            },
            providesTags: (_result, _error, {id}) => [{type: 'WebhookDeliveries', id}],
        }),
        getWebhookDeliveryStats: builder.query<WebhookDeliveryStats, {id: number, from?: string}>({
            query: ({id, from}) => ({
                url: `api/v1/webhooks/${id}/delivery-stats`,
                method: 'GET',
                params: {from},
            }),
            transformResponse: (response: {data: WebhookDeliveryStats}) => unwrapData(response),
            providesTags: (_result, _error, {id}) => [{type: 'WebhookDeliveries', id}],
        }),
        getWebhookDelivery: builder.query<WebhookDeliveryDetails, {id: number, deliveryId: string}>({
            query: ({id, deliveryId}) => ({
                url: `api/v1/webhooks/${id}/deliveries/${deliveryId}`,
                method: 'GET',
            }),
            transformResponse: (response: {data: WebhookDeliveryDetails}) => unwrapData(response),
            providesTags: (_result, _error, {deliveryId}) => [{type: 'WebhookDeliveries', id: deliveryId}],
        }),
        retryWebhookDelivery: builder.mutation<WebhookDelivery, {id: number, deliveryId: string}>({
            query: ({id, deliveryId}) => ({
                url: `api/v1/webhooks/${id}/deliveries/${deliveryId}/actions/retry`,
                method: 'POST',
            }),
            transformResponse: (response: {data: WebhookDelivery}) => unwrapData(response),
            invalidatesTags: (_result, _error, {id, deliveryId}) => [
                {type: 'WebhookDeliveries', id},
                {type: 'WebhookDeliveries', id: deliveryId},
            ],
        }),
        retryFailedWebhookDeliveries: builder.mutation<{queued: number}, {id: number, since: string}>({
            query: ({id, since}) => ({
                url: `api/v1/webhooks/${id}/actions/retry-failed`,
                method: 'POST',
                body: {since},
            }),
            transformResponse: (response: {data: {queued: number}}) => unwrapData(response),
            invalidatesTags: (_result, _error, {id}) => [{type: 'WebhookDeliveries', id}],
        }),
        sendWebhookTest: builder.mutation<WebhookDeliveryDetails, {id: number}>({
            query: ({id}) => ({
                url: `api/v1/webhooks/${id}/actions/send-test`,
                method: 'POST',
            }),
            transformResponse: (response: {data: WebhookDeliveryDetails}) => unwrapData(response),
            invalidatesTags: (_result, _error, {id}) => [{type: 'WebhookDeliveries', id}],
        }),
        createWebhookConfiguration: builder.mutation<WebhookConfiguration, { body: { status: WebhookStatus } }>({
            query: ({body}) => ({
                url: `/api/v1/webhook-configurations`,
                method: 'POST',
                body: body,
            }),
            transformResponse: (response: WebhookConfiguration | {data: WebhookConfiguration}) => unwrapData(response),
            invalidatesTags: ['SecretKey']
        }),
        getWebhookConfiguration: builder.query<{data: WebhookConfiguration[]}, void>({
            query: () => ({
                url: `/api/v1/webhook-configurations/`,
                method: 'GET',
            }),
            providesTags: ['SecretKey']
        }),
        generateSecretKey: builder.mutation<WebhookConfiguration, void>({
            query: () => ({
                url: `/api/v1/webhook-configurations/secretKey`,
                method: 'PUT',
            }),
            invalidatesTags: ['SecretKey']
        }),
        updateWebhookConfiguration: builder.mutation<WebhookConfiguration, { status: WebhookStatus }>({
            query: ({status}) => ({
                url: `/api/v1/webhook-configurations`,
                body: {status},
                method: 'PUT',
            }),
            invalidatesTags: ['SecretKey']
        }),
        getApiKeys: builder.query<{data: ApiKeySummary[]}, void>({
            query: () => ({
                url: `/api/v1/api-keys`,
                method: 'GET',
            
            }),
            providesTags: ['ApiKeys']
        }),

        getApiKey: builder.query<ApiKeySummary, number>({
            query: (id) => ({
                url: `/api/v1/api-keys/${id}`,
                method: 'GET',
            }),
            transformResponse: (response: ApiKeySummary | { data: ApiKeySummary }) => unwrapData(response),
            providesTags: (_result, _error, id) => [
                { type: 'ApiKeys', id },
                'ApiKeys'
            ],
        }),
        createApiKey: builder.mutation<CreatedApiKey, {data: ApiKeyCreateRequest}>({
            query: ({data}) => ({
                url: '/api/v1/api-keys',
                method: 'POST',
                body: data,
            }),
            transformResponse: (response: CreatedApiKey | { data: CreatedApiKey }) => unwrapData(response),
            invalidatesTags: ['ApiKeys']
        }),
        updateApiKey: builder.mutation<ApiKeySummary, {id: number, data: ApiKeyUpdateRequest}>({
            query: ({id, data}) => ({
                url: `/api/v1/api-keys/${id}`,
                method: 'PATCH',
                body: data,
            }),
            transformResponse: (response: ApiKeySummary | { data: ApiKeySummary }) => unwrapData(response),
            invalidatesTags: (_result, _error, {id}) => [
                { type: 'ApiKeys', id },
                'ApiKeys'
            ]
        }),
        deleteApiKey: builder.mutation<null, { id: number }>({
            query: ({id}) => ({
                url: `/api/v1/api-keys/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, {id}) => [
                { type: 'ApiKeys', id: id },
                'ApiKeys'
            ]
        }),
    }),
});

export const {
    useGetIntegrationsQuery,
    useGetCurrentGoogleIntegrationUsersQuery,
    useGetCurrentGoogleIntegrationQuery,
    useStartIntegrationGoogleMutation,
    useStartIntegrationPipedriveMutation,
    useImportGoogleContactsMutation,
    useDeleteGoogleIntegrationMutation,
    useDeleteIntegrationMutation,
    useStatusGoogleIntegrationMutation,
    useGetPipedriveSettingsQuery,
    usePatchPipedriveIntegrationMutation,
    useGetWebhooksQuery,
    useGetWebhookQuery,
    useUpdateWebhookMutation,
    useCreateWebhookMutation,
    useDeleteWebhookMutation,
    useGetWebhookDeliveriesQuery,
    useGetWebhookDeliveryQuery,
    useLazyGetWebhookDeliveryQuery,
    useRetryWebhookDeliveryMutation,
    useRetryFailedWebhookDeliveriesMutation,
    useSendWebhookTestMutation,
    useGetWebhookConfigurationQuery,
    useGetWebhookDeliveryStatsQuery,
    useCreateWebhookConfigurationMutation,
    useGenerateSecretKeyMutation,
    useUpdateWebhookConfigurationMutation,
    usePatchPipedrivePrefixMutation,
    useGetIntegrationImportsQuery,
    useGetApiKeysQuery,
    useGetApiKeyQuery,
    useCreateApiKeyMutation,
    useUpdateApiKeyMutation,
    useDeleteApiKeyMutation
} = integrationApi;
